import React, { useState, useEffect, lazy, Suspense } from 'react';
import { api } from '../services/api';

const LabelDesigner = lazy(() => import('../components/LabelDesigner'));

type LabelType = 'bin' | 'part' | 'drop' | 'order' | 'designer';

interface GeneratedLabel {
  type: LabelType;
  identifier: string;
  zpl: string;
  timestamp: Date;
}

interface Bin {
  id: string;
  bin_number: string;
  location?: string;
  status: string;
  barcode_printed_at?: string;
}

interface Template {
  id: string;
  name: string;
  labelType: string;
  zplTemplate: string;
  widthInches: number;
  heightInches: number;
  isDefault: boolean;
}

const LabelsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<LabelType>('bin');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Generated labels history
  const [generatedLabels, setGeneratedLabels] = useState<GeneratedLabel[]>([]);

  // Bins data
  const [bins, setBins] = useState<Bin[]>([]);
  const [selectedBinIds, setSelectedBinIds] = useState<string[]>([]);
  const [binsNeedingLabels, setBinsNeedingLabels] = useState<Bin[]>([]);

  // Templates
  const [templates, setTemplates] = useState<Template[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);



  // Form states
  const [binLabelForm, setBinLabelForm] = useState({
    binNumber: '',
    location: '',
    labelSize: '3x1',
  });

  const [partLabelForm, setPartLabelForm] = useState({
    partNumber: '',
    description: '',
    quantity: 1,
  });

  const [dropLabelForm, setDropLabelForm] = useState({
    dropId: '',
    partType: 'housing_main',
    length: 0,
    partNumber: '',
    color: '',
    fabricType: '',
    fabricColor: '',
    height: 0,
    width: 0,
  });

  const [templateForm, setTemplateForm] = useState({
    name: '',
    labelType: 'bin',
    zplTemplate: '',
    widthInches: 3,
    heightInches: 1,
    isDefault: false,
  });

  // Order Labels state
  const [orderSearch, setOrderSearch] = useState('');
  const [orderSearchResults, setOrderSearchResults] = useState<any[]>([]);
  const [orderSearching, setOrderSearching] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [selectedOrderLabelType, setSelectedOrderLabelType] = useState<string>('material');
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);
  const [orderLabelCopies, setOrderLabelCopies] = useState(1);
  const [orderLabelsLoading, setOrderLabelsLoading] = useState(false);

  // Print dialog state
  const [printDialog, setPrintDialog] = useState<{
    zpl: string;
    title: string;
    labelIds: string[];
    widthInches: number;
    heightInches: number;
  } | null>(null);
  const [labelPreviewUrl, setLabelPreviewUrl] = useState<string | null>(null);

  // ZPL preview state
  const [zplPreview, setZplPreview] = useState<string | null>(null);


  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [binsData, needLabels, templatesData] = await Promise.all([
        api.bins.getAll(),
        api.labels.getBinsNeedingLabels().catch(() => []),
        api.labels.getTemplates().catch(() => []),
      ]);
      setBins(binsData || []);
      setBinsNeedingLabels(needLabels || []);
      setTemplates(templatesData || []);
    } catch (err) {
      console.error('Failed to load initial data:', err);
    }
  };

  const showPrintDialog = (zpl: string, title: string, labelIds: string[] = [], widthInches = 4, heightInches = 6) => {
    setPrintDialog({ zpl, title, labelIds, widthInches, heightInches });
    // Generate visual preview via Labelary API (first label only)
    const firstLabel = zpl.split('^XZ')[0] + '^XZ';
    setLabelPreviewUrl(null);
    fetch(`https://api.labelary.com/v1/printers/12dpmm/labels/${widthInches}x${heightInches}/0/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: firstLabel,
    })
      .then(res => res.blob())
      .then(blob => setLabelPreviewUrl(URL.createObjectURL(blob)))
      .catch(() => setLabelPreviewUrl(null));
  };

  const handleBrowserPrint = () => {
    if (!labelPreviewUrl || !printDialog) return;
    const w = printDialog.widthInches;
    const h = printDialog.heightInches;
    const printWindow = window.open('', '_blank', `width=${w * 96 + 100},height=${h * 96 + 100}`);
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html>
<html><head><title>${printDialog.title}</title>
<style>
  @page { size: ${w}in ${h}in; margin: 0; }
  @media print { body { margin: 0; padding: 0; } }
  body { margin: 0; padding: 20px; display: flex; flex-direction: column; align-items: center; font-family: sans-serif; }
  img { width: ${w}in; height: ${h}in; object-fit: contain; }
  .info { margin-bottom: 10px; font-size: 12px; color: #666; }
  @media print { .info, .no-print { display: none; } }
</style></head><body>
  <p class="info">${printDialog.title} - ${w}" x ${h}"</p>
  <img src="${labelPreviewUrl}" alt="Label" />
  <p class="no-print" style="margin-top:16px;font-size:13px;color:#888;">
    Set paper size to ${w}" x ${h}" (or Avery label sheet) in print settings.
    <br/><button onclick="window.print()" style="margin-top:8px;padding:8px 24px;font-size:14px;cursor:pointer;background:#16a34a;color:white;border:none;border-radius:6px;">Print</button>
  </p>
</body></html>`);
    printWindow.document.close();
    // Auto-trigger print after image loads
    printWindow.onload = () => setTimeout(() => printWindow.print(), 300);
  };

  // Keep old name as alias so existing tab handlers work
  const showZplPopup = showPrintDialog;

  const handleGenerateBinLabel = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Find bin by number or generate ad-hoc label
      const bin = bins.find(b => b.bin_number === binLabelForm.binNumber);

      if (bin) {
        const result = await api.labels.generateBinLabel(bin.id, binLabelForm.labelSize);
        const newLabel: GeneratedLabel = {
          type: 'bin',
          identifier: result.binNumber,
          zpl: result.zpl,
          timestamp: new Date(),
        };
        setGeneratedLabels(prev => [newLabel, ...prev]);
        showZplPopup(result.zpl, `Bin Label: ${result.binNumber} (${binLabelForm.labelSize})`);
        setSuccess(`Generated ${binLabelForm.labelSize} label for bin ${result.binNumber}`);
      } else {
        // Generate ad-hoc bin label using part label generator
        const result = await api.labels.generatePartLabel(
          binLabelForm.binNumber,
          `Location: ${binLabelForm.location || 'N/A'}`,
          1
        );
        const newLabel: GeneratedLabel = {
          type: 'bin',
          identifier: binLabelForm.binNumber,
          zpl: result.zpl,
          timestamp: new Date(),
        };
        setGeneratedLabels(prev => [newLabel, ...prev]);
        showZplPopup(result.zpl, `Bin Label: ${binLabelForm.binNumber}`);
        setSuccess(`Generated ad-hoc label for ${binLabelForm.binNumber}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate bin label');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePartLabel = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await api.labels.generatePartLabel(
        partLabelForm.partNumber,
        partLabelForm.description,
        partLabelForm.quantity
      );
      const newLabel: GeneratedLabel = {
        type: 'part',
        identifier: result.partNumber,
        zpl: result.zpl,
        timestamp: new Date(),
      };
      setGeneratedLabels(prev => [newLabel, ...prev]);
      showZplPopup(result.zpl, `Part Label: ${result.partNumber}`);
      setSuccess(`Generated label for part ${result.partNumber}`);
    } catch (err: any) {
      setError(err.message || 'Failed to generate part label');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDropLabel = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const isFabricDrop = dropLabelForm.partType === 'fabric';
      const labelColor = isFabricDrop
        ? (dropLabelForm.fabricColor || undefined)
        : (dropLabelForm.color || undefined);
      const result = await api.labels.generateDropLabel(
        dropLabelForm.dropId,
        dropLabelForm.partType,
        dropLabelForm.length,
        dropLabelForm.partNumber || undefined,
        labelColor,
        isFabricDrop ? dropLabelForm.height || undefined : undefined,
        isFabricDrop ? dropLabelForm.width || undefined : undefined,
      );
      const newLabel: GeneratedLabel = {
        type: 'drop',
        identifier: result.dropId,
        zpl: result.zpl,
        timestamp: new Date(),
      };
      setGeneratedLabels(prev => [newLabel, ...prev]);
      showZplPopup(result.zpl, `Drop Label: ${result.dropId}`);
      setSuccess(`Generated label for drop ${result.dropId}`);
    } catch (err: any) {
      setError(err.message || 'Failed to generate drop label');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchPrintBins = async () => {
    if (selectedBinIds.length === 0) {
      setError('Please select bins to print labels for');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await api.labels.printBinLabelsBatch(selectedBinIds, binLabelForm.labelSize);

      // Add all labels to history
      result.labels.forEach(label => {
        const newLabel: GeneratedLabel = {
          type: 'bin',
          identifier: label.binNumber,
          zpl: label.zpl,
          timestamp: new Date(),
        };
        setGeneratedLabels(prev => [newLabel, ...prev]);
      });

      // Show combined ZPL
      const combinedZpl = result.labels.map(l => l.zpl).join('\n');
      showZplPopup(combinedZpl, `Batch Bin Labels (${result.count} labels)`);
      setSuccess(`Generated ${result.count} bin labels`);
      setSelectedBinIds([]);

      // Refresh bins needing labels
      const needLabels = await api.labels.getBinsNeedingLabels().catch(() => []);
      setBinsNeedingLabels(needLabels || []);
    } catch (err: any) {
      setError(err.message || 'Failed to generate batch labels');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await api.labels.createTemplate(templateForm);
      setSuccess('Template created successfully');
      setShowTemplateModal(false);
      setTemplateForm({
        name: '',
        labelType: 'bin',
        zplTemplate: '',
        widthInches: 3,
        heightInches: 1,
        isDefault: false,
      });

      // Refresh templates
      const templatesData = await api.labels.getTemplates().catch(() => []);
      setTemplates(templatesData || []);
    } catch (err: any) {
      setError(err.message || 'Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  const toggleBinSelection = (binId: string) => {
    setSelectedBinIds(prev =>
      prev.includes(binId)
        ? prev.filter(id => id !== binId)
        : [...prev, binId]
    );
  };

  const selectAllBinsNeedingLabels = () => {
    setSelectedBinIds(binsNeedingLabels.map(b => b.id));
  };

  // Order search with debounce
  const searchTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleOrderSearch = async (query: string) => {
    setOrderSearch(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (query.length < 2) {
      setOrderSearchResults([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      setOrderSearching(true);
      try {
        const result = await api.orders.getOrders({ search: query, limit: 10 });
        setOrderSearchResults(result.data || result.items || []);
      } catch (err) {
        console.error('Order search failed:', err);
        setOrderSearchResults([]);
      } finally {
        setOrderSearching(false);
      }
    }, 300);
  };

  const handleSelectOrder = async (order: any) => {
    setOrderSearchResults([]);
    setOrderSearch('');
    setLoading(true);
    try {
      const fullOrder = await api.orders.getOrder(order.id);
      setSelectedOrder(fullOrder);
      // Select all units by default
      if (fullOrder.items?.length) {
        setSelectedUnitIds(fullOrder.items.map((item: any) => item.id));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateOrderLabel = async () => {
    if (!selectedOrder) return;
    if (selectedUnitIds.length === 0 && selectedOrderLabelType !== 'shipping') {
      setError('Please select at least one unit');
      return;
    }
    setOrderLabelsLoading(true);
    setError(null);

    try {
      // Queue labels for the order
      const queuedLabels = await api.labels.queueOrderLabels(
        selectedOrder.id,
        selectedOrderLabelType,
        orderLabelCopies
      );

      if (!queuedLabels || queuedLabels.length === 0) {
        setError('No work orders found for this order. Has it been released to production?');
        setOrderLabelsLoading(false);
        return;
      }

      // Generate ZPL for each queued label and collect
      const zplResults: string[] = [];
      for (const label of queuedLabels) {
        try {
          const result = await api.labels.getZpl(label.id);
          zplResults.push(result.zpl);
          // Add to recent labels
          const newLabel: GeneratedLabel = {
            type: 'order',
            identifier: `${selectedOrder.orderNumber} - ${selectedOrderLabelType}`,
            zpl: result.zpl,
            timestamp: new Date(),
          };
          setGeneratedLabels(prev => [newLabel, ...prev]);
        } catch (err) {
          console.error(`Failed to generate ZPL for label ${label.id}:`, err);
        }
      }

      if (zplResults.length > 0) {
        const combinedZpl = zplResults.join('\n');
        const isShipping = selectedOrderLabelType === 'shipping';
        showPrintDialog(
          combinedZpl,
          `${selectedOrderLabelType.replace('_', ' ').toUpperCase()} Labels: ${selectedOrder.orderNumber} (${zplResults.length} labels)`,
          queuedLabels.map((l: any) => l.id),
          isShipping ? 4 : 3,
          isShipping ? 6 : 1
        );
        setSuccess(`Generated ${zplResults.length} ${selectedOrderLabelType.replace('_', ' ')} label(s) for ${selectedOrder.orderNumber}`);
      } else {
        setError('Failed to generate ZPL for labels');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate order labels');
    } finally {
      setOrderLabelsLoading(false);
    }
  };

  const handlePrintAllOrderLabels = async () => {
    if (!selectedOrder) return;
    setOrderLabelsLoading(true);
    setError(null);

    const allTypes = ['material', 'track', 'housing', 'roll_tube', 'bottom_bar', 'final_product', 'shipping'];
    const allZpl: string[] = [];
    const allLabelIds: string[] = [];
    let totalLabels = 0;

    try {
      for (const labelType of allTypes) {
        const queuedLabels = await api.labels.queueOrderLabels(
          selectedOrder.id,
          labelType,
          orderLabelCopies
        );
        for (const label of (queuedLabels || [])) {
          try {
            const result = await api.labels.getZpl(label.id);
            allZpl.push(result.zpl);
            allLabelIds.push(label.id);
            totalLabels++;
          } catch (err) {
            console.error(`ZPL generation failed for ${labelType}:`, err);
          }
        }
      }

      if (allZpl.length > 0) {
        const combinedZpl = allZpl.join('\n');
        showPrintDialog(
          combinedZpl,
          `ALL Labels: ${selectedOrder.orderNumber} (${totalLabels} labels)`,
          allLabelIds,
          3, 1
        );
        // Add summary to recent labels
        const newLabel: GeneratedLabel = {
          type: 'order',
          identifier: `${selectedOrder.orderNumber} - ALL (${totalLabels})`,
          zpl: combinedZpl,
          timestamp: new Date(),
        };
        setGeneratedLabels(prev => [newLabel, ...prev]);
        setSuccess(`Generated ${totalLabels} labels (all types) for ${selectedOrder.orderNumber}`);
      } else {
        setError('No labels generated. Has this order been released to production?');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate all order labels');
    } finally {
      setOrderLabelsLoading(false);
    }
  };

  const toggleUnitSelection = (unitId: string) => {
    setSelectedUnitIds(prev =>
      prev.includes(unitId)
        ? prev.filter(id => id !== unitId)
        : [...prev, unitId]
    );
  };

  const labelTypes = [
    { type: 'material' as const, name: 'Material', icon: '📦' },
    { type: 'track' as const, name: 'Track', icon: '🛤️' },
    { type: 'housing' as const, name: 'Housing', icon: '🏠' },
    { type: 'roll_tube' as const, name: 'Roll Tube', icon: '🎯' },
    { type: 'bottom_bar' as const, name: 'Bottom Bar', icon: '📏' },
    { type: 'final_product' as const, name: 'Final Product', icon: '✅' },
    { type: 'shipping' as const, name: 'Shipping', icon: '📮' },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Label Creator</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowTemplateModal(true)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Manage Templates
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-700 font-bold">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex justify-between items-center">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-700 font-bold">&times;</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        {[
          { id: 'bin', label: 'Bin Labels', icon: '📦' },
          { id: 'part', label: 'Part Labels', icon: '🔧' },
          { id: 'drop', label: 'Drop Labels', icon: '📐' },
          { id: 'order', label: 'Order Labels', icon: '📋' },
          { id: 'designer', label: 'Designer', icon: '🎨' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as LabelType)}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-green-500 text-green-600 dark:text-green-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Designer Tab - Full Width */}
      {activeTab === 'designer' && (
        <Suspense fallback={<div className="text-center py-12 text-gray-400">Loading designer...</div>}>
          <LabelDesigner />
        </Suspense>
      )}

      {/* Other Tabs - 3-column layout */}
      {activeTab !== 'designer' && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Form */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            {/* Bin Labels Tab */}
            {activeTab === 'bin' && (
              <div>
                <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Generate Bin Label</h2>

                {/* Bins needing labels */}
                {binsNeedingLabels.length > 0 && (
                  <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium text-yellow-800 dark:text-yellow-200">
                        Bins Needing Labels ({binsNeedingLabels.length})
                      </h3>
                      <div className="flex gap-2">
                        <button
                          onClick={selectAllBinsNeedingLabels}
                          className="text-sm px-3 py-1 bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded hover:bg-yellow-200 dark:hover:bg-yellow-700"
                        >
                          Select All
                        </button>
                        <button
                          onClick={handleBatchPrintBins}
                          disabled={selectedBinIds.length === 0 || loading}
                          className="text-sm px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          Print Selected ({selectedBinIds.length})
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      {binsNeedingLabels.map(bin => (
                        <label
                          key={bin.id}
                          className={`flex items-center gap-2 px-3 py-1 rounded cursor-pointer ${
                            selectedBinIds.includes(bin.id)
                              ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedBinIds.includes(bin.id)}
                            onChange={() => toggleBinSelection(bin.id)}
                            className="rounded"
                          />
                          {bin.bin_number}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <form onSubmit={handleGenerateBinLabel} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Bin Number
                    </label>
                    <input
                      type="text"
                      value={binLabelForm.binNumber}
                      onChange={e => setBinLabelForm(prev => ({ ...prev, binNumber: e.target.value }))}
                      placeholder="e.g., BIN-001"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter existing bin number or new bin identifier</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Location (optional)
                    </label>
                    <input
                      type="text"
                      value={binLabelForm.location}
                      onChange={e => setBinLabelForm(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="e.g., Aisle 3, Shelf B"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Label Size
                    </label>
                    <select
                      value={binLabelForm.labelSize}
                      onChange={e => setBinLabelForm(prev => ({ ...prev, labelSize: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="2x1">2" x 1" (Small)</option>
                      <option value="3x1">3" x 1" (Standard)</option>
                      <option value="3x2">3" x 2" (Medium)</option>
                      <option value="4x2">4" x 2" (Large)</option>
                      <option value="4x3">4" x 3" (X-Large)</option>
                      <option value="4x6">4" x 6" (Shipping)</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? 'Generating...' : 'Generate Bin Label'}
                  </button>
                </form>
              </div>
            )}

            {/* Part Labels Tab */}
            {activeTab === 'part' && (
              <div>
                <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Generate Part Label</h2>
                <form onSubmit={handleGeneratePartLabel} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Part Number *
                    </label>
                    <input
                      type="text"
                      value={partLabelForm.partNumber}
                      onChange={e => setPartLabelForm(prev => ({ ...prev, partNumber: e.target.value }))}
                      placeholder="e.g., 13100"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description (optional)
                    </label>
                    <input
                      type="text"
                      value={partLabelForm.description}
                      onChange={e => setPartLabelForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="e.g., Housing Top - Black"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Quantity
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={partLabelForm.quantity}
                      onChange={e => setPartLabelForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? 'Generating...' : 'Generate Part Label'}
                  </button>
                </form>
              </div>
            )}

            {/* Drop Labels Tab */}
            {activeTab === 'drop' && (
              <div>
                <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Generate Drop Label</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Generate labels for fabric or extrusion drops (leftover material pieces)
                </p>
                <form onSubmit={handleGenerateDropLabel} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Drop ID *
                    </label>
                    <input
                      type="text"
                      value={dropLabelForm.dropId}
                      onChange={e => setDropLabelForm(prev => ({ ...prev, dropId: e.target.value }))}
                      placeholder="e.g., DRP-20260212-0001"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Part Type *
                    </label>
                    <select
                      value={dropLabelForm.partType}
                      onChange={e => setDropLabelForm(prev => ({ ...prev, partType: e.target.value, color: '', fabricType: '', fabricColor: '' }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="housing_main">Housing Main</option>
                      <option value="v_track">V-Track</option>
                      <option value="weight_bar">Weight Bar</option>
                      <option value="u_channel">U-Channel</option>
                      <option value="bottom_bar">Bottom Bar</option>
                      <option value="housing_cover">Housing Cover</option>
                      <option value="screw_cover">Screw Cover</option>
                      <option value="keder_guide">Keder Guide</option>
                      <option value="roller_tube">Roller Tube</option>
                      <option value="keder">Keder Spline</option>
                      <option value="fabric">Fabric</option>
                    </select>
                  </div>

                  {/* Color for extrusions (frame colors) */}
                  {!['roller_tube', 'fabric', 'keder'].includes(dropLabelForm.partType) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Frame Color *
                      </label>
                      <select
                        value={dropLabelForm.color}
                        onChange={e => setDropLabelForm(prev => ({ ...prev, color: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      >
                        <option value="">Select color...</option>
                        <option value="Black">Black</option>
                        <option value="White">White</option>
                        <option value="Bronze">Bronze</option>
                        <option value="Tan">Tan</option>
                        <option value="Mill Finish">Mill Finish</option>
                        <option value="Custom">Custom Powder Coat</option>
                      </select>
                    </div>
                  )}

                  {/* Color for keder (black/white) */}
                  {dropLabelForm.partType === 'keder' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Color *
                      </label>
                      <select
                        value={dropLabelForm.color}
                        onChange={e => setDropLabelForm(prev => ({ ...prev, color: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      >
                        <option value="">Select color...</option>
                        <option value="Black">Black</option>
                        <option value="White">White</option>
                      </select>
                    </div>
                  )}

                  {/* Fabric type and color dropdowns */}
                  {dropLabelForm.partType === 'fabric' && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Fabric Type *
                          </label>
                          <select
                            value={dropLabelForm.fabricType}
                            onChange={e => setDropLabelForm(prev => ({ ...prev, fabricType: e.target.value, fabricColor: '' }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            required
                          >
                            <option value="">Select fabric...</option>
                            <option value="omega_pro_3">Omega Pro 3%</option>
                            <option value="omega_pro_5">Omega Pro 5%</option>
                            <option value="omega_pro_10">Omega Pro 10%</option>
                            <option value="twitchell_80">Twitchell 80%</option>
                            <option value="twitchell_90">Twitchell 90%</option>
                            <option value="twitchell_95">Twitchell 95%</option>
                            <option value="twitchell_nano">Twitchell Nano 50</option>
                            <option value="502_vinyl">502 Vinyl</option>
                            <option value="cvw">CVW</option>
                            <option value="insect">Insect Screen</option>
                            <option value="strataglass">Strataglass</option>
                            <option value="premium_solar">Premium Solar</option>
                            <option value="solar">Solar</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Fabric Color *
                          </label>
                          <select
                            value={dropLabelForm.fabricColor}
                            onChange={e => setDropLabelForm(prev => ({ ...prev, fabricColor: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            required
                          >
                            <option value="">Select color...</option>
                            {dropLabelForm.fabricType === 'omega_pro_3' && ['White', 'Grey', 'Charcoal', 'Black', 'Beige', 'Dark Bronze'].map(c => <option key={c} value={c}>{c}</option>)}
                            {dropLabelForm.fabricType === 'omega_pro_5' && ['White', 'Grey', 'Charcoal', 'Black', 'Beige', 'Dark Bronze'].map(c => <option key={c} value={c}>{c}</option>)}
                            {dropLabelForm.fabricType === 'omega_pro_10' && ['White', 'Grey', 'Charcoal', 'Black', 'Beige'].map(c => <option key={c} value={c}>{c}</option>)}
                            {dropLabelForm.fabricType === 'twitchell_80' && ['Black', 'Dark Bronze', 'Grey', 'Stucco'].map(c => <option key={c} value={c}>{c}</option>)}
                            {dropLabelForm.fabricType === 'twitchell_90' && ['Black', 'Dark Bronze', 'Grey', 'Stucco'].map(c => <option key={c} value={c}>{c}</option>)}
                            {dropLabelForm.fabricType === 'twitchell_95' && ['Black', 'Dark Bronze', 'Grey', 'Stucco'].map(c => <option key={c} value={c}>{c}</option>)}
                            {dropLabelForm.fabricType === 'twitchell_nano' && ['Black', 'Dark Bronze', 'Grey', 'Stucco'].map(c => <option key={c} value={c}>{c}</option>)}
                            {dropLabelForm.fabricType === '502_vinyl' && ['Clear', 'Smoke', 'Dark Grey'].map(c => <option key={c} value={c}>{c}</option>)}
                            {dropLabelForm.fabricType === 'cvw' && ['White', 'Ivory', 'Linen'].map(c => <option key={c} value={c}>{c}</option>)}
                            {dropLabelForm.fabricType === 'insect' && ['Black', 'Grey', 'Charcoal'].map(c => <option key={c} value={c}>{c}</option>)}
                            {dropLabelForm.fabricType === 'strataglass' && ['Clear'].map(c => <option key={c} value={c}>{c}</option>)}
                            {dropLabelForm.fabricType === 'premium_solar' && ['White', 'Grey', 'Charcoal', 'Black', 'Beige', 'Dark Bronze'].map(c => <option key={c} value={c}>{c}</option>)}
                            {dropLabelForm.fabricType === 'solar' && ['White', 'Grey', 'Charcoal', 'Black', 'Beige', 'Dark Bronze'].map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Width (inches) *
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.125"
                            value={dropLabelForm.width || ''}
                            onChange={e => setDropLabelForm(prev => ({ ...prev, width: parseFloat(e.target.value) || 0 }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Height (inches) *
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.125"
                            value={dropLabelForm.height || ''}
                            onChange={e => setDropLabelForm(prev => ({ ...prev, height: parseFloat(e.target.value) || 0 }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Length for non-fabric */}
                  {dropLabelForm.partType !== 'fabric' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Length ({dropLabelForm.partType === 'keder' ? 'yards' : 'inches'}) *
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.125"
                        value={dropLabelForm.length || ''}
                        onChange={e => setDropLabelForm(prev => ({ ...prev, length: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Part Number (optional)
                    </label>
                    <input
                      type="text"
                      value={dropLabelForm.partNumber}
                      onChange={e => setDropLabelForm(prev => ({ ...prev, partNumber: e.target.value }))}
                      placeholder="e.g., 13100"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? 'Generating...' : 'Generate Drop Label'}
                  </button>
                </form>
              </div>
            )}

            {/* Order Labels Tab */}
            {activeTab === 'order' && (
              <div>
                <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Generate Order Labels</h2>

                {/* Order Search */}
                <div className="relative mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Search Order
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={orderSearch}
                        onChange={e => handleOrderSearch(e.target.value)}
                        placeholder="Type order number (e.g., KS-14004)..."
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      {orderSearching && (
                        <div className="absolute right-3 top-2.5 text-gray-400">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Search results dropdown */}
                  {orderSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {orderSearchResults.map((order: any) => (
                        <button
                          key={order.id}
                          onClick={() => handleSelectOrder(order)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-medium text-gray-900 dark:text-white">{order.orderNumber}</span>
                              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">{order.dealerName}</span>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              order.status === 'in_production' ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' :
                              order.status === 'approved' || order.status === 'pending_release' ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' :
                              'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                            }`}>
                              {order.status?.replace(/_/g, ' ')}
                            </span>
                          </div>
                          {order.items && (
                            <span className="text-xs text-gray-400">{order.items.length} unit(s)</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Order Summary */}
                {selectedOrder && (
                  <div className="space-y-4">
                    {/* Order Info Bar */}
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                          <div>
                            <span className="text-lg font-bold text-green-800 dark:text-green-200">{selectedOrder.orderNumber}</span>
                            <span className="ml-3 text-sm text-green-700 dark:text-green-300">{selectedOrder.dealerName}</span>
                          </div>
                          <span className="text-sm text-green-600 dark:text-green-400">
                            {selectedOrder.items?.length || 0} Unit(s)
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            selectedOrder.status === 'in_production' ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' :
                            'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                          }`}>
                            {selectedOrder.status?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <button
                          onClick={() => { setSelectedOrder(null); setSelectedUnitIds([]); }}
                          className="text-sm px-3 py-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Change Order
                        </button>
                      </div>
                    </div>

                    {/* Label Type Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Label Type
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {labelTypes.map(lt => (
                          <button
                            key={lt.type}
                            onClick={() => setSelectedOrderLabelType(lt.type)}
                            className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                              selectedOrderLabelType === lt.type
                                ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 shadow-sm'
                                : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'
                            }`}
                          >
                            <span className="mr-1.5">{lt.icon}</span>
                            {lt.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Unit Selection */}
                    {selectedOrder.items?.length > 0 && (
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Units
                          </label>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setSelectedUnitIds(selectedOrder.items.map((i: any) => i.id))}
                              className="text-xs px-2 py-1 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                            >
                              Select All
                            </button>
                            <button
                              onClick={() => setSelectedUnitIds([])}
                              className="text-xs px-2 py-1 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                            >
                              Deselect All
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {selectedOrder.items.map((item: any, idx: number) => (
                            <label
                              key={item.id}
                              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                                selectedUnitIds.includes(item.id)
                                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                  : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedUnitIds.includes(item.id)}
                                onChange={() => toggleUnitSelection(item.id)}
                                className="rounded text-green-600"
                              />
                              <div className="flex-1">
                                <span className="font-medium text-gray-900 dark:text-white">
                                  Unit {item.unitNumber || idx + 1}
                                </span>
                                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                  {item.widthTop || item.width}"W × {item.heightLeft || item.height}"H
                                </span>
                              </div>
                              <div className="text-right text-sm">
                                <div className="text-gray-600 dark:text-gray-300">
                                  {item.fabricType?.replace(/_/g, ' ')} {item.fabricColor}
                                </div>
                                <div className="text-gray-400 dark:text-gray-500">
                                  {item.systemColor} system
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Copies + Action Buttons */}
                    <div className="flex items-end gap-3 pt-2">
                      <div className="w-24">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Copies
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="50"
                          value={orderLabelCopies}
                          onChange={e => setOrderLabelCopies(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <button
                        onClick={handleGenerateOrderLabel}
                        disabled={orderLabelsLoading || loading}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                      >
                        {orderLabelsLoading ? 'Generating...' : `Generate ${selectedOrderLabelType.replace('_', ' ')} Labels`}
                      </button>
                      <button
                        onClick={handlePrintAllOrderLabels}
                        disabled={orderLabelsLoading || loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium whitespace-nowrap"
                      >
                        {orderLabelsLoading ? 'Working...' : 'Print All Labels'}
                      </button>
                    </div>

                    {/* Help note */}
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      "Generate" creates the selected label type. "Print All" generates Material + Track + Housing + Roll Tube + Bottom Bar + Final Product + Shipping for every unit.
                    </p>
                  </div>
                )}

                {/* Empty state when no order selected */}
                {!selectedOrder && (
                  <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                    <svg className="mx-auto h-12 w-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p className="text-lg font-medium">Search for an order above</p>
                    <p className="text-sm mt-1">Enter an order number to generate labels for production</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - History & Info */}
        <div className="space-y-6">
          {/* Recent Labels */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Recent Labels</h3>
            {generatedLabels.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No labels generated yet this session</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {generatedLabels.slice(0, 10).map((label, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded"
                  >
                    <div>
                      <span className="text-xs uppercase font-medium text-gray-500 dark:text-gray-400">
                        {label.type}
                      </span>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{label.identifier}</p>
                    </div>
                    <button
                      onClick={() => showZplPopup(label.zpl, `${label.type}: ${label.identifier}`)}
                      className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                    >
                      View ZPL
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Help */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Quick Help</h3>
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
              <p><strong>ZPL Labels:</strong> Generated labels use ZPL (Zebra Programming Language) for Zebra thermal printers.</p>
              <p><strong>Barcodes:</strong> Labels include Code 128 barcodes for scanning.</p>
              <p><strong>Bin Labels:</strong> Multiple sizes from 2"×1" to 4"×6". Default is 3"×1".</p>
              <p><strong>Order Labels:</strong> Search by order number, select label type, and generate for all units. Order must be released to production first.</p>
              <p><strong>Print All:</strong> Generates all 7 label types for every unit in one batch.</p>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Label Templates</h2>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>

              {/* Existing Templates */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Existing Templates</h3>
                {templates.length === 0 ? (
                  <p className="text-sm text-gray-500">No templates configured</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {templates.map(template => (
                      <div
                        key={template.id}
                        className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{template.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {template.labelType} • {template.widthInches}" × {template.heightInches}"
                            {template.isDefault && ' • Default'}
                          </p>
                        </div>
                        <button
                          onClick={() => setZplPreview(template.zplTemplate)}
                          className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 rounded hover:bg-gray-300"
                        >
                          View ZPL
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Create Template Form */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Create New Template</h3>
                <form onSubmit={handleCreateTemplate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Template Name
                      </label>
                      <input
                        type="text"
                        value={templateForm.name}
                        onChange={e => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Label Type
                      </label>
                      <select
                        value={templateForm.labelType}
                        onChange={e => setTemplateForm(prev => ({ ...prev, labelType: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm"
                      >
                        <option value="bin">Bin</option>
                        <option value="part">Part</option>
                        <option value="drop">Drop</option>
                        <option value="material">Material</option>
                        <option value="track">Track</option>
                        <option value="housing">Housing</option>
                        <option value="final_product">Final Product</option>
                        <option value="shipping">Shipping</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Width (inches)
                      </label>
                      <input
                        type="number"
                        step="0.25"
                        value={templateForm.widthInches}
                        onChange={e => setTemplateForm(prev => ({ ...prev, widthInches: parseFloat(e.target.value) || 3 }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Height (inches)
                      </label>
                      <input
                        type="number"
                        step="0.25"
                        value={templateForm.heightInches}
                        onChange={e => setTemplateForm(prev => ({ ...prev, heightInches: parseFloat(e.target.value) || 1 }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={templateForm.isDefault}
                          onChange={e => setTemplateForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Set as default</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      ZPL Template
                    </label>
                    <textarea
                      value={templateForm.zplTemplate}
                      onChange={e => setTemplateForm(prev => ({ ...prev, zplTemplate: e.target.value }))}
                      rows={8}
                      placeholder="^XA&#10;^FO50,50^A0N,30,30^FD[TEXT]^FS&#10;^XZ"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm font-mono"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Use placeholders like [ORDER], [PART], [BARCODE], [LENGTH], etc.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowTemplateModal(false)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {loading ? 'Creating...' : 'Create Template'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ZPL Preview Modal (for templates) */}
      {zplPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">ZPL Preview</h2>
                <button
                  onClick={() => setZplPreview(null)}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
              <pre className="p-4 bg-gray-100 dark:bg-gray-900 rounded text-sm font-mono overflow-x-auto whitespace-pre-wrap">
                {zplPreview}
              </pre>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(zplPreview);
                    setSuccess('ZPL copied to clipboard');
                    setZplPreview(null);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Copy ZPL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Dialog Modal */}
      {printDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{printDialog.title}</h2>
                <button
                  onClick={() => { setPrintDialog(null); setLabelPreviewUrl(null); }}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-xl"
                >
                  ✕
                </button>
              </div>

              {/* Label Preview */}
              <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                {labelPreviewUrl ? (
                  <img
                    src={labelPreviewUrl}
                    alt="Label Preview"
                    className="mx-auto max-h-48 border border-gray-300 dark:border-gray-600 rounded bg-white"
                  />
                ) : (
                  <div className="py-8 text-gray-400 dark:text-gray-500">
                    <svg className="animate-spin h-8 w-8 mx-auto mb-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <p className="text-sm">Generating preview...</p>
                  </div>
                )}
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                  Preview of first label ({printDialog.labelIds.length} total)
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mb-4">
                <button
                  onClick={handleBrowserPrint}
                  disabled={!labelPreviewUrl}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                >
                  Print Label ({printDialog.widthInches}" x {printDialog.heightInches}")
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(printDialog.zpl);
                    setSuccess('ZPL code copied to clipboard');
                  }}
                  className="px-4 py-3 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 font-medium"
                >
                  Copy ZPL
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Opens a print window sized to {printDialog.widthInches}" x {printDialog.heightInches}". Use Avery labels or set paper size in print settings.
              </p>

              {/* ZPL Code Toggle */}
              <details className="border border-gray-200 dark:border-gray-700 rounded-lg">
                <summary className="px-4 py-2 cursor-pointer text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                  View Raw ZPL Code
                </summary>
                <pre className="p-4 bg-gray-100 dark:bg-gray-900 text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-48">
                  {printDialog.zpl}
                </pre>
              </details>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabelsPage;
